import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ContactType } from '@signalvote/shared';
import type { FinanceContactDto } from '@signalvote/shared';
import { trafficStatusToCss } from './trafficUtils';
import type { TrafficStatus } from './trafficUtils';

interface ContactCardProps {
  contact: FinanceContactDto;
  onClick: (contactId: string) => void;
}

export function ContactCard({ contact, onClick }: ContactCardProps) {
  const isDonor = contact.contactType === ContactType.Donor;
  const count = isDonor ? contact.contributionCount : contact.paymentCount;
  const countLabel = isDonor ? 'contributions' : 'payments';
  // #427 — the contact-level follow-up window (followUpStatus / daysRemaining) was retired with
  // the EmployerInfoRequestDate column; best-efforts is now a per-gift audit signal. The card's
  // "Missing" badge (ruleset-aware) still surfaces an itemized donor's gap.
  // Backend-computed, ruleset-aware: non-empty only for an itemized (over-threshold)
  // donor missing a field their jurisdiction requires — so an under-threshold or
  // AL-employer/occupation case no longer false-flags.
  const showMissing = (contact.missingItemizedFields ?? []).length > 0;
  const dotColor = trafficStatusToCss(contact.trafficStatus as TrafficStatus);

  return (
    <button
      type="button"
      onClick={() => onClick(contact.id)}
      className="flex w-full items-center gap-3 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-accent/50"
    >
      <span
        data-testid="traffic-dot"
        className={cn('h-3 w-3 shrink-0 rounded-full', dotColor)}
      />
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-sm">{contact.name}</span>
        {showMissing && (
          <Badge variant="destructive" className="ml-2 text-xs">Missing</Badge>
        )}
        {contact.employer && (
          <span className="ml-2 text-xs text-muted-foreground">
            {contact.employer}{contact.occupation ? ` — ${contact.occupation}` : ''}
          </span>
        )}
      </div>
      <Badge variant="secondary">{isDonor ? 'Donor' : 'Payee'}</Badge>
      <div className="text-right text-sm">
        <div className="font-medium">${contact.calendarYearTotal.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground">{count} {countLabel}</div>
      </div>
    </button>
  );
}
